import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../../components/session/SessionManager';
import { portfolioService } from '../../../services/api/portfolioService';
import { Position, PortfolioResponse } from '../../../types/portfolio';
import { isApiError } from '../../../services/api/client';
import './PositionsSummary.css';

interface PositionsSummaryProps {
  onPositionSelect?: (position: Position) => void;
  onRefreshReady?: (refreshFn: () => void) => void;
}

export const PositionsSummary: React.FC<PositionsSummaryProps> = ({ onPositionSelect, onRefreshReady }) => {
  const sessionData = useSession();
  const navigate = useNavigate();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Fetch open positions
  const fetchPositions = useCallback(async () => {
    if (!sessionData?.isAuthenticated || !sessionData?.currentAccount?.accountId) {
      setPositions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: PortfolioResponse = await portfolioService.getOpenPositions({
        accountId: sessionData.currentAccount.accountId,
        limit: 50
      });

      setPositions(response.positions || []);
    } catch (err) {
      console.error('Error fetching positions:', err);
      if (isApiError(err)) {
        setError(`Failed to load positions: ${err.message}`);
      } else {
        setError('Failed to load positions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionData?.isAuthenticated, sessionData?.currentAccount?.accountId]);

  // Format position data for wireframe display
  const formatPositionForWireframe = (position: Position) => {
    const formatted = portfolioService.formatPositionForUI(position);
    
    // Format expiry time to simple format like "14:35"
    const expiryTime = new Date(position.expiry_time);
    const timeString = `${expiryTime.getHours().toString().padStart(2, '0')}:${expiryTime.getMinutes().toString().padStart(2, '0')}`;
    
    // Get simple instrument symbol (remove prefix if present)
    const instrumentSymbol = position.instrumentId.replace('ins_', '').toUpperCase();
    
    // Format P&L to currency format like "+$12.50" or "-$25.00"
    const pnlAmount = position.current_pnl?.amount || 0;
    const pnlFormatted = pnlAmount >= 0 
      ? `+$${Math.abs(pnlAmount).toFixed(2)}`
      : `-$${Math.abs(pnlAmount).toFixed(2)}`;
    
    // Format stake like "$50 stake"
    const stakeFormatted = `$${position.stake.toFixed(0)} stake`;
    
    return {
      ...position,
      instrumentSymbol,
      productDisplayName: formatted.productDisplayName,
      stakeFormatted,
      pnlFormatted,
      isProfit: pnlAmount >= 0,
      entryFormatted: position.entry_spot.toFixed(4),
      currentFormatted: position.current_spot?.toFixed(4) || 'N/A',
      expiryFormatted: timeString,
      growthFormatted: position.product === 'accumulator' && position.product_params.growth_rate 
        ? `${position.product_params.growth_rate}%` 
        : null,
      ticksFormatted: position.product === 'accumulator' && position.product_params.tick_count 
        ? `${position.product_params.tick_count}/∞` 
        : null,
      takeProfitFormatted: position.product === 'accumulator' && position.product_params.take_profit 
        ? `$${position.product_params.take_profit}` 
        : null,
    };
  };

  // Initial fetch
  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
  }, [fetchPositions]);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(fetchPositions);
    }
  }, [onRefreshReady, fetchPositions]);

  // Get recent positions (latest 3)
  const recentPositions = positions.slice(0, 3);
  const hasMorePositions = positions.length > 3;

  // Handle navigation to portfolio page
  const handleShowAll = () => {
    navigate('/portfolio');
  };

  return (
    <section className="positions-summary">
      <div className="positions-header">
        <div className="positions-title">
          <h3>Open Positions ({loading ? '...' : positions.length})</h3>
          {hasMorePositions && !isCollapsed && (
            <button 
              className="show-all-btn"
              onClick={handleShowAll}
              title="View all positions in Portfolio"
            >
              Show All
            </button>
          )}
        </div>
        <button 
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? '▼' : '▲'}
        </button>
      </div>
      
      {!isCollapsed && (
        <div className="position-cards">
          {loading && positions.length === 0 ? (
            // Show skeleton loading for wireframe compliance
            <>
              <div className="position-card">
                <div className="position-header">
                  <div>
                    <div className="position-instrument">Loading...</div>
                    <div className="position-type">Loading...</div>
                  </div>
                  <div className="position-pnl">...</div>
                </div>
                <div className="position-details">
                  <span>Entry: ...</span>
                  <span>Current: ...</span>
                  <span>Expires: ...</span>
                </div>
              </div>
            </>
          ) : error ? (
            <div className="position-card">
              <div className="position-header">
                <div>
                  <div className="position-instrument">Error</div>
                  <div className="position-type">Failed to load positions</div>
                </div>
                <div className="position-pnl">-</div>
              </div>
              <div className="position-details">
                <span onClick={fetchPositions} style={{ cursor: 'pointer', color: 'var(--glacier-blue)' }}>
                  Click to retry
                </span>
              </div>
            </div>
          ) : positions.length === 0 ? (
            <div className="position-card">
              <div className="position-header">
                <div>
                  <div className="position-instrument">No Positions</div>
                  <div className="position-type">No open positions</div>
                </div>
                <div className="position-pnl">$0.00</div>
              </div>
              <div className="position-details">
                <span>Start trading to see positions here</span>
              </div>
            </div>
          ) : (
            recentPositions.map((position) => {
              const formatted = formatPositionForWireframe(position);

              return (
                <div 
                  className="position-card"
                  key={position.positionId}
                  onClick={() => onPositionSelect?.(position)}
                  style={{ cursor: onPositionSelect ? 'pointer' : 'default' }}
                >
                  <div className="position-header">
                    <div>
                      <div className="position-instrument">{formatted.instrumentSymbol}</div>
                      <div className="position-type">
                        {formatted.productDisplayName} • {formatted.stakeFormatted}
                      </div>
                    </div>
                    <div className={`position-pnl ${formatted.isProfit ? 'positive' : 'negative'}`}>
                      {formatted.pnlFormatted}
                    </div>
                  </div>
                  
                  <div className="position-details">
                    <span>Entry: {formatted.entryFormatted}</span>
                    <span>Current: {formatted.currentFormatted}</span>
                    
                    {/* Show appropriate details based on product type */}
                    {position.product === 'accumulator' ? (
                      <>
                        {formatted.growthFormatted && <span>Growth: {formatted.growthFormatted}</span>}
                        {formatted.ticksFormatted && <span>Ticks: {formatted.ticksFormatted}</span>}
                        {formatted.takeProfitFormatted && <span>Take Profit: {formatted.takeProfitFormatted}</span>}
                      </>
                    ) : (
                      <span>Expires: {formatted.expiryFormatted}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </section>
  );
};
